'use client';

/**
 * 知识库列表页右侧数据表格（`/knowledge-bases/list` 模块内实现）。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Selection, SortDescriptor } from 'react-aria-components';
import type { SortingState, PaginationState } from '@hs/ui';
import {
  AlertDialog,
  Button,
  cn,
  Dropdown,
  Label,
  ListBox,
  Pagination,
  SearchField,
  Separator,
  toast,
} from '@heroui/react';
import { DataGrid, InlineSelect } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import {
  IconDotsVertical as MoreVertical,
  IconFolderMinus,
  IconFolderPlus,
  IconRefresh as RefreshCw,
  IconTrash as Trash2,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { formatRelativeTime } from '@hs/shared/utils/formatTime';
import type { KnowledgeBasesScope } from '../../components/KnowledgeBaseHeader';
import {
  KNOWLEDGE_BASE_SIDEBAR_ALL_ID,
  KNOWLEDGE_BASE_SIDEBAR_UNCATEGORIZED_ID,
} from '../../components/constants';
import { CategoryPickerModal } from './CategoryPickerModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const ROW_ACTIONS_ID = '__actions__';
const DEFAULT_PAGE_SIZE = 20;

const FILE_FORMAT_LABELS: Record<string, string> = {
  doc: '文档',
  slides: '幻灯片',
  poster: '海报',
};

function sortingToDescriptor(sorting: SortingState): SortDescriptor | undefined {
  const s = sorting[0];
  if (!s) return undefined;
  return { column: s.id, direction: s.desc ? 'descending' : 'ascending' };
}

function descriptorToSorting(descriptor: SortDescriptor | undefined): SortingState {
  if (!descriptor?.column) return [];
  return [{ id: String(descriptor.column), desc: descriptor.direction === 'descending' }];
}

function buildPaginationPages(totalPages: number, safePage: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push('ellipsis');
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }
  return pages;
}

function selectionToKnowledgeBaseIds(selectedKeys: Selection, items: KnowledgeBaseRow[]): string[] {
  if (selectedKeys === 'all') {
    return items.map((r) => r.id).filter(Boolean) as string[];
  }
  if (selectedKeys instanceof Set) {
    return [...selectedKeys].map(String);
  }
  return [];
}

export type KnowledgeBaseRow = {
  id: string;
  title?: string | null;
  fileFormat?: 'slides' | 'doc' | 'poster' | null;
  content?: unknown;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export interface KnowledgeBasesDataListProps {
  scope: KnowledgeBasesScope;
  selectedCategoryId: string | null;
  selectedCategoryName: string | null;
  onOpenSlides: (row: KnowledgeBaseRow) => void;
}

export function KnowledgeBasesDataList({
  scope,
  selectedCategoryId,
  selectedCategoryName,
  onOpenSlides,
}: KnowledgeBasesDataListProps) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'updatedAt', desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());
  const [isBatchDeleteDialogOpen, setIsBatchDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeBaseRow | null>(null);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [removeFromCategoryDialogOpen, setRemoveFromCategoryDialogOpen] = useState(false);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setSelectedKeys(new Set());
  }, [scope, selectedCategoryId]);

  const sortBy = useMemo(() => sorting[0]?.id ?? undefined, [sorting]);
  const sortOrder = useMemo(
    () => (sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined),
    [sorting],
  );

  const titleWhere = useMemo(
    () =>
      searchKeyword.trim()
        ? ({ title: { $like: `%${searchKeyword.trim()}%` } } as Record<string, unknown>)
        : undefined,
    [searchKeyword],
  );

  const isAllView =
    selectedCategoryId == null || selectedCategoryId === KNOWLEDGE_BASE_SIDEBAR_ALL_ID;
  const isUncategorizedView = selectedCategoryId === KNOWLEDGE_BASE_SIDEBAR_UNCATEGORIZED_ID;
  const isRealCategorySelected =
    !isAllView && !isUncategorizedView && selectedCategoryId != null;

  const listMyQuery = trpc.knowledgeBases.listMy.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy:
        sortBy && ['title', 'contentType', 'createdAt', 'updatedAt'].includes(sortBy)
          ? (sortBy as 'title' | 'contentType' | 'createdAt' | 'updatedAt')
          : undefined,
      sortOrder,
      where: titleWhere,
    } as never,
    { enabled: scope === 'my' && isAllView },
  );

  const listSharedQuery = trpc.knowledgeBases.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy:
        sortBy && ['title', 'contentType', 'createdAt', 'updatedAt'].includes(sortBy)
          ? (sortBy as 'title' | 'contentType' | 'createdAt' | 'updatedAt')
          : undefined,
      sortOrder,
      isSharedToSpace: true,
      where: titleWhere,
    } as never,
    { enabled: scope === 'shared' && isAllView },
  );

  const listUncategorizedQuery = trpc.knowledgeBases.listUncategorized.useQuery(
    {
      dataType: scope === 'shared' ? 'share' : 'my',
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy:
        sortBy && ['createdAt', 'updatedAt', 'title'].includes(sortBy)
          ? (sortBy as 'createdAt' | 'updatedAt' | 'title')
          : undefined,
      sortOrder,
    } as never,
    { enabled: isUncategorizedView },
  );

  const listByCategoryQuery = trpc.knowledgeBases.listByCategory.useQuery(
    {
      categoryId: selectedCategoryId!,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy:
        sortBy && ['createdAt', 'updatedAt', 'title'].includes(sortBy)
          ? (sortBy as 'createdAt' | 'updatedAt' | 'title')
          : undefined,
      sortOrder,
    } as never,
    { enabled: isRealCategorySelected },
  );

  const { data, isLoading, refetch } = isUncategorizedView
    ? listUncategorizedQuery
    : isAllView
      ? scope === 'my'
        ? listMyQuery
        : listSharedQuery
      : listByCategoryQuery;

  const items = (data?.items ?? []) as KnowledgeBaseRow[];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / (pagination.pageSize || DEFAULT_PAGE_SIZE)));

  const invalidateLists = useCallback(async () => {
    await Promise.all([
      utils.knowledgeBases.listMy.invalidate(),
      utils.knowledgeBases.list.invalidate(),
      utils.knowledgeBases.listByCategory.invalidate(),
      utils.knowledgeBases.listUncategorized.invalidate(),
      utils.knowledgeBases.listLatestCategoryDetails.invalidate(),
      utils.categories.getCategoryTree.invalidate(),
    ]);
  }, [
    utils.knowledgeBases.listMy,
    utils.knowledgeBases.list,
    utils.knowledgeBases.listByCategory,
    utils.knowledgeBases.listUncategorized,
    utils.knowledgeBases.listLatestCategoryDetails,
    utils.categories.getCategoryTree,
  ]);

  const deleteMutation = trpc.knowledgeBases.delete.useMutation({
    onSuccess: async () => {
      await invalidateLists();
      void refetch();
    },
    onError: () => {
      toast.danger('删除失败');
    },
  });

  const addToCategoriesMutation = trpc.knowledgeBases.addKnowledgeBasesToCategories.useMutation({
    onSuccess: async (data) => {
      toast.success(`已添加 ${data.count ?? 0} 条分类关联`);
      await invalidateLists();
      void refetch();
      setCategoryPickerOpen(false);
      setSelectedKeys(new Set());
    },
    onError: () => {
      toast.danger('添加至分类失败');
    },
  });

  const removeFromCategoriesMutation = trpc.knowledgeBases.removeKnowledgeBasesFromCategories.useMutation({
    onSuccess: async (data) => {
      toast.success(`已移除 ${data.count ?? 0} 条分类关联`);
      await invalidateLists();
      void refetch();
      setRemoveFromCategoryDialogOpen(false);
      setSelectedKeys(new Set());
    },
    onError: () => {
      toast.danger('从分类移除失败');
    },
  });

  const handleOpen = useCallback(
    (row: KnowledgeBaseRow) => {
      if (row.fileFormat === 'doc') {
        navigate(`/knowledge-bases/doc/${row.id}`);
        return;
      }
      if (row.fileFormat === 'slides') {
        onOpenSlides(row);
        return;
      }
      toast.warning('该格式暂不支持从此处打开');
    },
    [navigate, onOpenSlides],
  );

  const renderActions = useCallback(
    (row: KnowledgeBaseRow) => (
      <div className="flex justify-center">
        <Dropdown>
          <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
            <MoreVertical className="size-4" stroke={1.5} />
          </Button>
          <Dropdown.Popover className="min-w-[120px]" placement="bottom end">
            <Dropdown.Menu>
              <Dropdown.Item id="open" textValue="打开" onAction={() => handleOpen(row)}>
                <Label>打开</Label>
              </Dropdown.Item>
              <Dropdown.Item
                id="delete"
                textValue="删除"
                variant="danger"
                onAction={() => setDeleteTarget(row)}
              >
                <Label>删除</Label>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      </div>
    ),
    [handleOpen],
  );

  const dataColumns = useMemo<DataGridColumn<KnowledgeBaseRow>[]>(
    () => [
      {
        id: 'title',
        accessorKey: 'title',
        header: '标题',
        minWidth: 280,
        allowsSorting: true,
        allowsResizing: true,
        isRowHeader: true,
        cell: (row) => <span className="text-sm font-medium">{row.title?.trim() ? row.title : '未命名'}</span>,
      },
      {
        id: 'fileFormat',
        accessorKey: 'fileFormat',
        header: '类型',
        minWidth: 100,
        allowsSorting: false,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">
            {FILE_FORMAT_LABELS[row.fileFormat ?? ''] ?? row.fileFormat ?? '—'}
          </span>
        ),
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: '创建时间',
        minWidth: 140,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">
            {row.createdAt ? formatRelativeTime(row.createdAt) : '—'}
          </span>
        ),
      },
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: '更新时间',
        minWidth: 140,
        allowsSorting: true,
        allowsResizing: true,
        cell: (row) => (
          <span className="text-muted-foreground text-sm">
            {row.updatedAt ? formatRelativeTime(row.updatedAt) : '—'}
          </span>
        ),
      },
    ],
    [],
  );

  const actionsColumn = useMemo<DataGridColumn<KnowledgeBaseRow>>(
    () => ({
      id: ROW_ACTIONS_ID,
      header: '',
      align: 'end',
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      allowsSorting: false,
      allowsResizing: false,
      pinned: 'end',
      cell: (item) => (
        <div className="flex min-w-0 justify-end gap-1 overflow-hidden">{renderActions(item)}</div>
      ),
    }),
    [renderActions],
  );

  const columns = useMemo(() => [...dataColumns, actionsColumn], [dataColumns, actionsColumn]);

  const sortableColumnIds = useMemo(() => {
    if (isRealCategorySelected || isUncategorizedView) {
      return new Set(['createdAt', 'updatedAt', 'title']);
    }
    return new Set(['title', 'contentType', 'createdAt', 'updatedAt']);
  }, [isRealCategorySelected, isUncategorizedView]);

  const columnsWithRowHeader = useMemo(() => {
    const first = columns.find((c) => c.id !== ROW_ACTIONS_ID);
    const fh = first?.id;
    return columns.map((c) => ({
      ...c,
      isRowHeader: c.id === fh,
      allowsSorting: sortableColumnIds.has(String(c.id)) ? true : (c.allowsSorting ?? false),
    }));
  }, [columns, sortableColumnIds]);

  const sortDescriptor = useMemo(() => sortingToDescriptor(sorting), [sorting]);

  const handleSortChange = useCallback((d: SortDescriptor) => {
    setSorting(descriptorToSorting(d));
  }, []);

  const handleRefresh = useCallback(() => void refetch(), [refetch]);

  const selectedCount =
    selectedKeys === 'all' ? items.length : selectedKeys instanceof Set ? selectedKeys.size : 0;

  const handleBatchDeleteClick = useCallback(() => {
    if (selectedCount === 0) {
      toast.warning('请先选择要删除的条目');
      return;
    }
    setIsBatchDeleteDialogOpen(true);
  }, [selectedCount]);

  const handleConfirmBatchDelete = useCallback(async () => {
    const ids = selectionToKnowledgeBaseIds(selectedKeys, items);
    if (ids.length === 0) return;
    for (const id of ids) {
      await deleteMutation.mutateAsync({ id });
    }
    toast.success(`已删除 ${ids.length} 条`);
    setIsBatchDeleteDialogOpen(false);
    setSelectedKeys(new Set());
  }, [selectedKeys, items, deleteMutation]);

  const handleAddToCategoriesConfirm = useCallback(
    async (categoryIds: string[]) => {
      const kbIds = selectionToKnowledgeBaseIds(selectedKeys, items);
      if (kbIds.length === 0 || categoryIds.length === 0) return;
      const payload = kbIds.flatMap((knowledgeBaseId) =>
        categoryIds.map((categoryId) => ({ knowledgeBaseId, categoryId })),
      );
      try {
        await addToCategoriesMutation.mutateAsync(payload as never);
      } catch {
        /* onError */
      }
    },
    [selectedKeys, items, addToCategoriesMutation],
  );

  const handleConfirmRemoveFromCategory = useCallback(async () => {
    if (!selectedCategoryId || !isRealCategorySelected) return;
    const kbIds = selectionToKnowledgeBaseIds(selectedKeys, items);
    if (kbIds.length === 0) return;
    const payload = kbIds.map((knowledgeBaseId) => ({
      knowledgeBaseId,
      categoryId: selectedCategoryId,
    }));
    try {
      await removeFromCategoriesMutation.mutateAsync(payload as never);
    } catch {
      /* onError */
    }
  }, [
    selectedCategoryId,
    isRealCategorySelected,
    selectedKeys,
    items,
    removeFromCategoriesMutation,
  ]);

  const safePage = Math.min(pagination.pageIndex + 1, pageCount);
  const paginationPages = useMemo(() => buildPaginationPages(pageCount, safePage), [pageCount, safePage]);
  const pageSizes = [10, 20, 50, 100];

  const getRowId = useCallback((row: KnowledgeBaseRow) => String(row.id ?? ''), []);

  const scopeLabel = scope === 'my' ? '我的' : '共享';
  const headerTitle =
    selectedCategoryName != null && String(selectedCategoryName).trim() !== ''
      ? `${scopeLabel} · ${selectedCategoryName}`
      : `${scopeLabel} · 全部`;

  return (
    <>
      <div className="flex h-14 shrink-0 items-center border-b border-[var(--border)] px-4">
        <h1 className="text-2xl font-bold">{headerTitle}</h1>
      </div>
      <div className={cn('bg-background flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4')}>
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={cn(
              'grid min-h-0 min-w-0 w-full flex-1 gap-y-3',
              'grid-rows-[auto_minmax(0,1fr)_auto]',
            )}
          >
            <div className="relative z-10 flex min-w-0 shrink-0 flex-col gap-3">
              <div className="flex w-full min-w-0 shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <SearchField
                    aria-label="搜索标题"
                    value={searchKeyword}
                    onChange={(v) => {
                      setSearchKeyword(v);
                      setPagination((p) => ({ ...p, pageIndex: 0 }));
                    }}
                  >
                    <SearchField.Group>
                      <SearchField.SearchIcon />
                      <SearchField.Input
                        className="w-[min(100%,220px)] min-w-[160px]"
                        placeholder="搜索标题"
                      />
                      <SearchField.ClearButton />
                    </SearchField.Group>
                  </SearchField>
                  <Button variant="secondary" onPress={handleRefresh}>
                    <RefreshCw className="size-4" stroke={1.5} />
                    刷新
                  </Button>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    isDisabled={selectedCount === 0 || addToCategoriesMutation.isPending}
                    onPress={() => setCategoryPickerOpen(true)}
                  >
                    <IconFolderPlus className="size-4" stroke={1.5} />
                    添加至分类
                  </Button>
                  <Button
                    variant="secondary"
                    isDisabled={
                      selectedCount === 0 ||
                      !isRealCategorySelected ||
                      removeFromCategoriesMutation.isPending
                    }
                    onPress={() => setRemoveFromCategoryDialogOpen(true)}
                  >
                    <IconFolderMinus className="size-4" stroke={1.5} />
                    从分类中移除
                  </Button>
                  <Button
                    variant="danger"
                    isDisabled={selectedCount === 0 || deleteMutation.isPending}
                    onPress={handleBatchDeleteClick}
                  >
                    <Trash2 className="size-4" stroke={1.5} />
                    删除
                  </Button>
                </div>
              </div>
            </div>

            <div className="relative min-h-0 min-w-0 overflow-hidden">
              <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col">
                {isLoading ? (
                  <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center text-sm">
                    加载中…
                  </div>
                ) : null}
                <DataGrid<KnowledgeBaseRow>
                  allowsColumnResize
                  aria-label="知识库列表"
                  className="flex h-full min-h-0 min-w-0 flex-col"
                  columns={columnsWithRowHeader}
                  contentClassName="min-w-0"
                  data={items}
                  defaultSelectedKeys={undefined}
                  getRowId={getRowId}
                  renderEmptyState={() => (
                    <div className="text-muted-foreground py-8 text-center text-sm">暂无数据</div>
                  )}
                  scrollContainerClassName={cn(
                    'h-full max-h-full min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain',
                    '[&_[data-slot=table-resizable-container]]:overflow-visible',
                    '[&_[data-slot=table-content]]:!overflow-visible',
                    '[&_thead_th]:bg-[var(--surface-secondary)] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20',
                    '[&_th]:min-w-0 [&_td]:min-w-0',
                    '[&_[role=columnheader]]:min-w-0 [&_[role=gridcell]]:min-w-0',
                  )}
                  selectedKeys={selectedKeys}
                  selectionMode="multiple"
                  showSelectionCheckboxes
                  sortDescriptor={sortDescriptor}
                  variant="primary"
                  onSelectionChange={setSelectedKeys}
                  onSortChange={handleSortChange}
                />
              </div>
            </div>

            <div className="text-muted-foreground flex min-w-0 shrink-0 flex-nowrap items-center justify-between gap-3 overflow-x-auto text-sm">
              <div className="flex w-fit shrink-0 items-center">
                <Pagination className="w-fit max-w-none" size="sm">
                  <Pagination.Content className="flex w-fit min-w-0">
                    <Pagination.Item>
                      <Pagination.Previous
                        isDisabled={safePage <= 1}
                        onPress={() =>
                          setPagination((p) => ({
                            ...p,
                            pageIndex: Math.max(0, p.pageIndex - 1),
                          }))
                        }
                      >
                        <Pagination.PreviousIcon />
                      </Pagination.Previous>
                    </Pagination.Item>
                    {paginationPages.map((p, i) =>
                      p === 'ellipsis' ? (
                        <Pagination.Item key={`e-${i}`}>
                          <Pagination.Ellipsis />
                        </Pagination.Item>
                      ) : (
                        <Pagination.Item key={p}>
                          <Pagination.Link
                            isActive={p === safePage}
                            onPress={() =>
                              setPagination((prev) => ({
                                ...prev,
                                pageIndex: (p as number) - 1,
                              }))
                            }
                          >
                            {p}
                          </Pagination.Link>
                        </Pagination.Item>
                      ),
                    )}
                    <Pagination.Item>
                      <Pagination.Next
                        isDisabled={safePage >= pageCount}
                        onPress={() =>
                          setPagination((p) => ({
                            ...p,
                            pageIndex: Math.min(pageCount - 1, p.pageIndex + 1),
                          }))
                        }
                      >
                        <Pagination.NextIcon />
                      </Pagination.Next>
                    </Pagination.Item>
                  </Pagination.Content>
                </Pagination>
              </div>

              <div className="flex h-4 w-fit shrink-0 flex-nowrap items-center gap-3 whitespace-nowrap">
                <InlineSelect
                  aria-label="每页条数"
                  value={String(pagination.pageSize)}
                  onChange={(v) => {
                    if (v) {
                      setPagination((p) => ({
                        ...p,
                        pageSize: Number(v),
                        pageIndex: 0,
                      }));
                    }
                  }}
                >
                  <InlineSelect.Trigger>
                    <span className="text-muted">每页</span>
                    <InlineSelect.Value />
                    <InlineSelect.Indicator />
                  </InlineSelect.Trigger>
                  <InlineSelect.Popover className="w-[88px]">
                    <ListBox>
                      {pageSizes.map((n) => (
                        <ListBox.Item key={n} id={String(n)} textValue={String(n)}>
                          {n}
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </InlineSelect.Popover>
                </InlineSelect>
                <Separator className="!h-4 shrink-0" orientation="vertical" />
                <span className="text-muted shrink-0">
                  第 {total === 0 ? 0 : (safePage - 1) * pagination.pageSize + 1}–
                  {Math.min(safePage * pagination.pageSize, total)} 条，共 {total} 条
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CategoryPickerModal
        open={categoryPickerOpen}
        onOpenChange={setCategoryPickerOpen}
        scope={scope}
        isSubmitting={addToCategoriesMutation.isPending}
        onConfirm={handleAddToCategoriesConfirm}
      />

      <AlertDialog
        isOpen={removeFromCategoryDialogOpen}
        onOpenChange={(open) => {
          setRemoveFromCategoryDialogOpen(open);
        }}
      >
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>从分类中移除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定将已选中的 {selectedCount} 条知识库从「
                {selectedCategoryName?.trim() ? selectedCategoryName : '当前分类'}」中移除吗？不会删除知识库本身。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setRemoveFromCategoryDialogOpen(false)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={removeFromCategoriesMutation.isPending}
                  onPress={() => void handleConfirmRemoveFromCategory()}
                >
                  {removeFromCategoriesMutation.isPending ? '移除中…' : '确认移除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <AlertDialog
        isOpen={isBatchDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsBatchDeleteDialogOpen(open);
          if (!open) setSelectedKeys(new Set());
        }}
      >
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除已选中的 {selectedCount} 条知识库吗？删除后不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setIsBatchDeleteDialogOpen(false);
                    setSelectedKeys(new Set());
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteMutation.isPending}
                  onPress={() => {
                    void handleConfirmBatchDelete();
                  }}
                >
                  {deleteMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <AlertDialog isOpen={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>删除确认</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除「{deleteTarget?.title?.trim() ? deleteTarget.title : '未命名'}」吗？删除后将不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setDeleteTarget(null)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteMutation.isPending}
                  onPress={() => {
                    if (!deleteTarget?.id) return;
                    void (async () => {
                      try {
                        await deleteMutation.mutateAsync({ id: deleteTarget.id });
                        toast.success('已删除');
                        setDeleteTarget(null);
                      } catch {
                        /* onError */
                      }
                    })();
                  }}
                >
                  {deleteMutation.isPending ? '删除中…' : '删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </>
  );
}
